import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  CardComponent,
  Columns,
  DashedStandardDropdown,
  IceBurgChart,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useStores } from '../../stores';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import axios from 'axios';
import properties from '../../core-utils/properties';
import {
  formatRiskStratificationData,
  // getLabels,
  titleAndSubtitles,
} from '../../core-utils/helper-utils/riskProfileHelper';
import Loader from '../../_components/molecules/Loader';
import { observer } from 'mobx-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { pageScrollDown } from '../SummaryPage';
import { PATIENT_DETAILS } from '../../core-utils/routes';
import moment from 'moment';
import CustomColumnsTable from '../../_components/organisms/CustomColumnsTable';
import {
  columnsDropdownOptions,
  defaultColumns,
  formatMembersListData,
  getColumns,
  triggerCondDefaultColumns,
} from '../../core-utils/helper-utils/customColumnsHelper';
import { handleDownloadCSV } from '../../core-utils/helper-utils/downloadPdfHelper';
import Snackbar from '../../_components/atoms/Snackbar';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { getInfoIcon } from '../../core-utils/helper-utils/infoIconHelper';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { getLast12MonthsRange } from '../CareQualityPage';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import {
  checkUserAccess,
  UserAccessConstants,
} from '../../core-utils/helper-utils/userAccess';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModalNew';
import CohortFilterBar from '../../_components/molecules/CohortFilterBar';
import { getCohortMembersCount } from '../../core-utils/axios/Cohort/Summary';
import { useCohortOnPageLoad } from '../../core-utils/custom-hooks/useCohortPageOnLoad';
import { useCohortModal } from '../../core-utils/custom-hooks/useCohortModal';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 0px 66px 63px',
    width: '1182px',
  },
  flex: {
    display: 'flex',
  },
  duration: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '230px',
    height: '27px',
    background: patientAccessTheme.palette.status.white,
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    borderRadius: '4px',
    color: patientAccessTheme.palette.text.highemphasis,
    padding: '0px',
  },
});

export interface RiskProfilingProps {
  type: 'chronic' | 'trigger' | 'cost' | 'utilization' | 'drugs';
}

const RiskProfiling: React.FC<RiskProfilingProps> = observer(({ type }) => {
  const { classes } = useStyles();
  const employerGroupStore = useStores().employerGroupStore;
  const memberSummaryStore = useStores().memberSummaryStore;
  const navigate = useNavigate();
  const defaultSelectedColumns =
    type === 'trigger' ? triggerCondDefaultColumns : defaultColumns;

  const location: any = useLocation();
  const locationState = location.state;
  const quarterlyReportStore = useStores().quaterlyReportStore;
  const loginPageStore = useStores().loginPageStore;
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quarterlyReportStore;
  const { open, openModal, closeModal, isEditMode } = useCohortModal();
  const { totalResults, memberIds, isInitializing } = useCohortOnPageLoad({
    fetchTotalResults: () =>
      getCohortMembersCount(
        employerGroupStore.employerGroupResponseData.eg_nid,
        aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
        employerGroupStore.employerGroupResponseData.max_claim_date,
      ),
    enabled:
      isPlanDatesFetched &&
      !!employerGroupStore?.employerGroupResponseData?.eg_nid?.[0],
  });

  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const abortController = new AbortController();
  const payload = useAPIPayload();

  const breadcrumbs = [
    [
      'text',
      locationState?.isRiskStrat ? 'Summary' : 'Risk Profile',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(-1);
      },
    ],
    [
      'text',
      titleAndSubtitles[type]?.title,
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const infoIcon = getInfoIcon(
    `Top 3 Diagnosis based on Total Cost.`,
    () => {},
    {
      display: 'flex',
      alignItems: 'center',
    },
  );

  //states
  const [totalPages, setTotalPages] = useState(0);

  const [pageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);

  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [riskStratificationData, setRiskStratificationData] = useState<any[]>(
    [],
  );
  const [selectedRiskCategory, setSelectedRiskCategory] = useState<any>();
  const [riskMemberListData, setRiskMemberListData] = useState<any[]>([]);
  const [clickedMember, setClickedMember] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    defaultSelectedColumns,
  );
  const [tableColumns, setTableColumns] = useState<Columns[]>(
    getColumns(defaultSelectedColumns, infoIcon),
  );
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const [sortField, setSortField] = useState<string>('totalCost');
  const [sortOrder, setSortOrder] = useState<number>(-1);

  //Functions
  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setPageNumber(page);
  };

  const handleMemberClick = (value: string) => {
    setClickedMember(value);
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };
  const handleChangeColumns = () => {
    setTableColumns(getColumns(selectedColumns, infoIcon));
    selectedRiskCategory?.memberIds?.length > 0
      ? getRiskMembersList()
      : setRiskMemberListData([]);
  };

  const handleSort = (event: any, column: any, item: any) => {
    const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
    const { sortColumnName } = column;
    if (!sortColumnName) {
      return;
    }

    setSortField(sortColumnName);
    setSortOrder(sortOrder);
  };

  const handleExportCSV = () => {
    handleDownloadCSV(
      properties.aiqBaseUrl + properties.membersList + '/cohort-csv',
      { ...membersListRequestBody, showAll: true },
      `${selectedRiskCategory?.title}.csv`,
      handleOpenSnackBar,
    );
  };
  const getStartAndEndDates = (
    max_claim_date: string,
  ): { start_date: string; to_date: string } => {
    const maxDate = moment(max_claim_date);

    const toDate = maxDate
      .clone()
      .startOf('month')
      .add(1, 'month')
      .subtract(1, 'day');

    const startDate = toDate.clone().add(1, 'day').subtract(1, 'year');

    return {
      start_date: startDate.format('YYYY-MM-DD'),
      to_date: toDate.format('YYYY-MM-DD'),
    };
  };

  //APIs
  const getRiskStratification = async () => {
    let endpoint: string = properties.riskPrevalence;
    switch (type) {
      case 'chronic':
        endpoint = properties.riskPrevalence;
        break;
      case 'trigger':
        endpoint = properties.riskTriggerConditions;
        break;
      case 'cost':
        endpoint = properties.riskCost;
        break;
      case 'utilization':
        endpoint = properties.riskUtilization;
        break;
      case 'drugs':
        endpoint = properties.riskDrugs;
        break;
      default:
        endpoint;
    }
    const response = await axios.post(
      properties.aiqBaseUrl + properties.summaryBaseUrl + endpoint,
      {
        startDate:
          aggregationParameter === 'paid' ? paidStartDate : incurredStartDate,
        endDate:
          aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
        claim_id_list: [],
        member_unique_id_list: cohortDetailsStore.memberUniqueIdList || [],
        max_claim_date:
          employerGroupStore?.employerGroupResponseData?.max_claim_date,
        ...payload,
      },
      { signal: abortController.signal },
    );
    return response.data;
  };

  const getMemberClaims = async (memberId: string) => {
    return await axios
      .post(
        properties.aiqBaseUrl +
          properties.membersBaseUrl +
          properties.memberDetails,
        {
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          paidStartDate,
          paidEndDate,
          incurredStartDate,
          incurredEndDate,
          aggregationParameter,
          member_unique_id: [memberId],
          claim_id_list: [],
        },
        { signal: abortController.signal },
      )
      .then((response) => response.data);
  };

  const membersListRequestBody = {
    eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
    member_unique_id_list: selectedRiskCategory?.memberIds ?? [],
    pageNumber: pageNumber - 1,
    pageSize: pageSize,
    sortField: sortField,
    sortOrder: sortOrder,
    category: 'chronic',
    max_claim_date: employerGroupStore.employerGroupResponseData.max_claim_date,
    columnList: selectedColumns,
    diagnosis_count: 3,
    diagnosis_sort_field: 'totalPaid',
    incurredStartDate: '',
    incurredEndDate: '',
    paidStartDate: getStartAndEndDates(
      employerGroupStore.employerGroupResponseData.max_claim_date,
    ).start_date,
    paidEndDate: getStartAndEndDates(
      employerGroupStore.employerGroupResponseData.max_claim_date,
    ).to_date,
    aggregationParameter: 'paid',
  };

  const getRiskMembersList = async () => {
    setTableLoading(true);

    const response = await axios.post(
      properties.aiqBaseUrl + properties.membersListCohort,
      membersListRequestBody,
      { signal: abortController.signal },
    );

    if (response?.status === 200) {
      const updatedData = await Promise.all(
        response?.data?.data?.memberList?.map(async (item: any) => {
          const memberClaimsData = await getMemberClaims(item['Member ID']);
          return {
            ...item,
            claimData: memberClaimsData?.data,
          };
        }),
      );

      setRiskMemberListData(
        formatMembersListData(updatedData, selectedColumns),
      );
      setTableLoading(false);
    }
  };

  const apiCalls = async () => {
    setLoading(true);
    const response = await getRiskStratification();
    let data = [];
    switch (type) {
      case 'chronic':
        data = response?.data?.riskPrevalence;
        break;
      case 'trigger':
        data = response?.data?.riskTrigger;
        break;
      case 'cost':
        data = response?.data?.riskCost;
        break;
      case 'utilization':
        data = response?.data?.riskUtilization;
        break;
      case 'drugs':
        data = response?.data?.riskConsumption;
        break;
      default:
        data = response?.data?.riskPrevalence;
    }
    setRiskStratificationData(formatRiskStratificationData(data, type));
    setLoading(false);
  };

  //useEffects
  useEffect(() => {
    if (isInitializing) {
      return;
    }
    apiCalls();
    return () => {
      abortController.abort();
    };
  }, [
    isInitializing,
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
    cohortDetailsStore.conditionsDetails,
  ]);

  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    if (locationState?.isRiskStrat) {
      setSelectedRiskCategory(
        riskStratificationData?.find(
          (item) => item?.rawTier === locationState.tier,
        ),
      );
      setTotalPages(
        Math.ceil(
          riskStratificationData?.find(
            (item) => item?.rawTier === locationState.tier,
          )?.memberIds?.length / pageSize,
        ),
      );
    } else {
      //set the tier 1 data as default category
      setSelectedRiskCategory(
        riskStratificationData?.find((item) => item?.tier === 1),
      );
      setTotalPages(
        Math.ceil(
          riskStratificationData?.find((item) => item?.tier === 1)?.memberIds
            ?.length / pageSize,
        ),
      );
    }
  }, [riskStratificationData]);

  useEffect(() => {
    selectedRiskCategory?.memberIds?.length > 0
      ? getRiskMembersList()
      : (setRiskMemberListData([]), setSelectedColumns(defaultSelectedColumns));
  }, [selectedRiskCategory, pageNumber, sortField, sortOrder]);

  useEffect(() => {
    if (clickedMember) {
      memberSummaryStore.setPathDetails([
        [
          'text',
          titleAndSubtitles[type]?.title,
          {
            color: patientAccessTheme.palette.primary[500],
            fontWeight: 500,
          },
        ],
      ]);
      navigate(PATIENT_DETAILS, { state: { memberId: clickedMember } });
    }
  }, [clickedMember]);

  return isPlanDatesLoading || loading || isInitializing ? (
    <Loader />
  ) : (
    <>
      <Grid className={classes.root}>
        <Grid style={{ marginBottom: '19px' }}>
          <Breadcrumbs itemList={breadcrumbs} />
        </Grid>
        <PageTitleAndSubTitle
          title={titleAndSubtitles[type]?.title}
          chip={
            <Grid className={classes.flex}>
              <Typography
                fontSize="17px"
                lineHeight="21px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="semiBold"
                margin="2px 8px 0px 0px"
              >
                for
              </Typography>
              <Grid className={classes.duration}>
                {getLast12MonthsRange(
                  employerGroupStore.employerGroupResponseData.max_claim_date,
                )}
              </Grid>
            </Grid>
          }
          subTitle={titleAndSubtitles[type]?.subTitle}
          showQueryDataButton={checkUserAccess(
            UserAccessConstants.COHORT_DETAILS,
            loginPageStore.userResponse.role.name,
            loginPageStore.userFeatures,
          )}
          queryButtonText={
            cohortDetailsStore.cohortDetailsList?.length > 0 &&
            cohortDetailsStore.cohortDetailsList[0].category !== ''
              ? 'Edit Cohort'
              : 'Add Cohort'
          }
          queryDataAction={() => {
            if (
              checkUserAccess(
                UserAccessConstants.COHORT_DETAILS,
                loginPageStore.userResponse.role.name,
                loginPageStore.userFeatures,
              )
            ) {
              openModal();
            }
          }}
        />
        <CohortFilterBar />
        <CardComponent
          title={'Risk Categories'}
          subTitle={'To understand the driving factors of risk members'}
          width="1176px"
          body={
            <Grid>
              <IceBurgChart
                tiersData={riskStratificationData}
                indicatorWidth="400px"
                handleOnClick={(tier: number) => {
                  setSelectedRiskCategory(
                    riskStratificationData?.find((item) => item?.tier === tier),
                  );
                  setTotalPages(
                    Math.ceil(
                      riskStratificationData?.find(
                        (item) => item?.tier === tier,
                      )?.memberIds?.length / pageSize,
                    ),
                  );
                  setPageNumber(1);
                  const element = document.getElementById('member-details');
                  if (element) {
                    // 👇 Will scroll smoothly to the top of the given element id section
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }}
              />
            </Grid>
          }
          padding="20px 40px 24px 25px"
          mainGridStyles={{
            marginTop: '24px',
            marginBottom: '41px',
          }}
        />
        <Grid id="member-details">
          <CustomColumnsTable
            title={
              <Grid className={classes.flex}>
                <Typography
                  fontSize="16px"
                  lineHeight="24px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  fontType="semiBold"
                  margin="2px 8px 0px 0px"
                >
                  Members by
                </Typography>
                <DashedStandardDropdown
                  options={riskStratificationData?.map((item) => item?.title)}
                  displayValue={riskStratificationData?.map(
                    (item) => item?.title,
                  )}
                  value={selectedRiskCategory?.title}
                  handleChange={(event: any) => {
                    const riskCategory = event.target.value;
                    setSelectedRiskCategory(
                      riskStratificationData?.find(
                        (item) => item?.title === riskCategory,
                      ),
                    );
                    setTotalPages(
                      Math.ceil(
                        riskStratificationData?.find(
                          (item) => item?.title === riskCategory,
                        )?.memberIds?.length / pageSize,
                      ),
                    );
                    setPageNumber(1);
                  }}
                />
              </Grid>
            }
            subTitle={'View the members by Risk Categories'}
            options={columnsDropdownOptions}
            mandateOptions={['Member ID']}
            selectedItems={selectedColumns}
            setSelectedItems={setSelectedColumns}
            handleChangeColumns={handleChangeColumns}
            handleExport={handleExportCSV}
            columns={tableColumns}
            rowsData={riskMemberListData}
            tableLoading={tableLoading}
            handleMemberClick={handleMemberClick}
            handleSort={handleSort}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            pageNumber={pageNumber}
          />
        </Grid>
        <div id="scroll-top-element" />
        {pageScrollDown('scroll-top-element')}
      </Grid>

      <CohortDetailsModal
        open={open}
        handleClose={closeModal}
        totalResults={totalResults}
        isEditMode={isEditMode}
        existingMemberUniqueIds={memberIds}
      />
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default RiskProfiling;
